import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useDataAuth } from '../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { fetchServiceJson } from '../../services/common.services';
import { formatNumber } from '../../utils/invoice';
import moment from 'moment';

export const CurrencyExchangeSection = forwardRef(({ dto, handleInputChange, handleDirectInputChange, setRefreshTotals }, ref) => {
    const fieldsRequired =  ['other_currency', 'exchange_rate_date', 'exchange_rate'];
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const [coins, setCoins] = useState([]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchServiceJson("MENU-INVOICE", userSession.token);
                if (!response?.data) return;
                response.data.forEach((item) => {
                    try {
                        if (item.field === "coins") {
                            setCoins(JSON.parse(item.value));
                        }
                    } catch (jsonError) {
                        console.error("Error parsing JSON for item:", item, jsonError);
                    }
                });
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [userSession.token]);

    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));
    
    useEffect(() => {
        setRefreshTotals(true)
        handleCalculate({ target: { value: dto.exchange_rate } })
    }, [dto.total_retentions, dto.products])

    const handleCalculate = ({ target: { value } }) => {
        if (value.length === 0) return;
        const rate = parseFloat(dto.exchange_rate);
        if (!rate || rate === 0) return;
        const parseAndConvert = (field) => {
            const amount = parseFloat(dto[field]);
            return !isNaN(amount) && amount > 0 ? (amount / rate).toFixed(4) : null;
        };
        const discountOther = parseAndConvert('discount');
        if (discountOther !== null) {
            handleDirectInputChange('discount_other', discountOther);
        }
        const retentionsOther = parseAndConvert('total_retentions');
        if (retentionsOther !== null) {
            handleDirectInputChange('retentions_total_other', retentionsOther);
        }
        const payableOther = parseAndConvert('payable_amount');
        if (payableOther !== null) {
            handleDirectInputChange('payable_amount_other', payableOther);
        }
        const taxAmount = parseAndConvert('tax_amount');
        if (taxAmount !== null) {
            handleDirectInputChange('tax_amount_other', taxAmount);
        }
        const grossTotalOther = parseAndConvert('gross_total_value');
        if (grossTotalOther !== null) {
            handleDirectInputChange('gross_total_value_other', grossTotalOther);
        }
        setRefreshTotals(true);
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12}}>
                            <FormControl fullWidth required size="small"
                                error={Boolean(errorInputs.other_currency?.isError)}
                            >
                                <InputLabel>{t('commonFields.currency')}</InputLabel>
                                <Select label={t('commonFields.currency')} name="other_currency"
                                    value={dto.other_currency}
                                    onChange={handleInputChange}
                                >
                                    {coins.map((coin, i) => <MenuItem key={i} value={coin.code}>{coin.name}</MenuItem>)}
                                </Select>
                                {errorInputs.other_currency?.isError && (<FormHelperText>{errorInputs.other_currency.messageError}</FormHelperText>)}
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12}}>
                            <TextField fullWidth required type="number" size="small" name='exchange_rate' label={t('commonFields.exchangeRate')} className='input-no-spinner'
                                value={dto.exchange_rate || 0}
                                error={Boolean(errorInputs.exchange_rate?.isError)}
                                helperText={errorInputs.exchange_rate?.messageError || ''}
                                onChange={handleInputChange}
                                onBlur={handleCalculate}
                            />
                        </Grid>

                        <Grid size={{ xs: 12}}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker name="exchange_rate_date" label={t('commonFields.exchangeRateDate')}
                                    maxDate={new Date()}
                                    value={dto.exchange_rate_date}
                                    onChange={(e) => handleChangeDate(e, 'exchange_rate_date')}
                                    inputFormat="dd/MM/yyyy"
                                    renderInput={(params) => <TextField {...params} size="small" fullWidth InputLabelProps={{ shrink: true }} />}
                                />
                            </LocalizationProvider>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                        sx={{
                            backgroundColor: '#f5f5f5',
                            p: 2,
                            paddingLeft: 1,
                            borderRadius: 2,
                            boxShadow: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            {t('crm.products.discountTotal')}:
                            <Typography component="span" fontWeight={500}> {formatNumber(dto.discount_other)}</Typography>
                        </Typography>

                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            {t('commonFields.retentions')}:
                            <Typography component="span" fontWeight={500}> {formatNumber(dto.retentions_total_other)}</Typography>
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            {t('crm.products.taxTotal')}:
                            <Typography component="span" fontWeight={500}> {formatNumber(dto.tax_amount_other)}</Typography>
                        </Typography>

                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                            {t('commonFields.grossTotalValue')}: 
                            <Typography component="span" fontWeight={500}> {formatNumber(dto.gross_total_value_other)}</Typography>
                        </Typography>

                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {t('crm.products.generalTotal')}: {formatNumber(dto.payable_amount_other || 0)}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
});
